import React from "react";
import { Card, ProgressBar, Button, Badge } from "react-bootstrap";
import { PriceWithDiscount } from "../../../components/PriceWithDiscount";

/**
 * Displays the student's hour balance, expiry, and a renewal CTA.
 * CTA is shown when expires_in_days < 14 OR hours_available < 20% of hours_total.
 */
function BalanceCard({ balance, onRenew }) {
  if (!balance) return null;

  // Backend contract: hours_balance / hours_purchased / hours_consumed (strings)
  // + active_subscriptions[] (array). Map to numbers for display + progress.
  const active = balance.active_subscriptions?.[0] ?? null;
  const hoursAvailable = Number(balance.hours_balance ?? 0);
  const hoursTotal = Number(balance.hours_purchased ?? 0);
  const hoursConsumed = Number(balance.hours_consumed ?? 0);

  // Days until the active subscription expires (if it has an end date).
  let expiresInDays = null;
  if (active?.end_date) {
    const diff = Math.ceil(
      (new Date(active.end_date) - new Date()) / (1000 * 60 * 60 * 24)
    );
    expiresInDays = diff;
  }

  const pct =
    hoursTotal > 0 ? Math.round((hoursAvailable / hoursTotal) * 100) : 0;
  const lowBalance = hoursTotal > 0 && pct < 20;
  const nearExpiry = expiresInDays != null && expiresInDays < 14;
  const showCta = lowBalance || nearExpiry;

  const barVariant = pct < 20 ? "danger" : pct < 50 ? "warning" : "success";

  return (
    <Card className="mb-4">
      <Card.Body>
        <h5 className="card-title">Mi saldo de horas</h5>

        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
          <span className="fs-3 fw-bold" data-testid="hours-available">
            {hoursAvailable} h
          </span>
          <Badge bg="secondary">
            {active?.package_name ?? "Sin paquete activo"}
          </Badge>
        </div>

        {active?.agreed_price != null && (
          <div className="mb-2">
            <PriceWithDiscount
              listPrice={active.list_price}
              discountPct={active.discount_pct}
              finalPrice={active.agreed_price}
              size="sm"
            />
          </div>
        )}

        <ProgressBar
          now={pct}
          variant={barVariant}
          label={`${pct}%`}
          className="mb-2"
          aria-label="Horas disponibles"
        />

        <div className="d-flex gap-4 text-muted small mb-3 flex-wrap">
          <span>
            Total: <strong>{hoursTotal} h</strong>
          </span>
          <span>
            Consumidas: <strong>{hoursConsumed} h</strong>
          </span>
          {expiresInDays != null && (
            <span className={nearExpiry ? "text-danger fw-semibold" : ""}>
              Vence en:{" "}
              <strong data-testid="expires-in-days">
                {expiresInDays} días
              </strong>
            </span>
          )}
        </div>

        {showCta && (
          <Button
            variant="primary"
            className="w-100"
            style={{ minHeight: 44 }}
            onClick={onRenew}
            data-testid="renew-cta"
          >
            Renovar paquete
          </Button>
        )}
      </Card.Body>
    </Card>
  );
}

export { BalanceCard };
